import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";

const inputCls =
  "w-full rounded-lg border-2 border-[#F5F2EB] bg-[#141414] px-4 py-2.5 text-sm font-mono text-[#F5F2EB] placeholder-[#F5F2EB]/40 transition-shadow focus:shadow-[3px_3px_0_#F5F2EB] focus:outline-none";

export const InquiryModal = ({ product, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", address: "", offer: "", message: "" });
  const [sending, setSending] = useState(false);
  const [buying, setBuying] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertSent, setAlertSent] = useState(false);

  const submitAlert = async (e) => {
    e.preventDefault();
    try {
      await api.post("/alerts", { product_id: product.id, email: alertEmail });
      setAlertSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const buyNow = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        product_id: product.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBuying(false);
    }
  };

  useEffect(() => {
    if (product) setForm({ name: "", email: "", address: "", offer: "", message: "" });
  }, [product]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", {
        product_id: product.id,
        ...form,
        offer: form.offer ? parseFloat(form.offer) : null,
      });
      toast.success("Inquiry sent — JFLY will get back to you shortly.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const sold = product?.status === "sold";

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          data-testid="inquiry-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 md:p-8"
        >
          <div className="absolute inset-0 bg-black/70" onClick={onClose} />
          <motion.div
            initial={{ y: 48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 48, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-lenis-prevent
            className="relative z-10 grid max-h-[90vh] w-full max-w-4xl grid-cols-1 overflow-y-auto rounded-2xl border-2 border-[#F5F2EB] bg-[#141414] shadow-[8px_8px_0_#F5F2EB] md:grid-cols-2"
          >
            <div className="relative min-h-[240px] border-b-2 border-[#F5F2EB] md:min-h-full md:border-b-0 md:border-r-2">
              <img src={product.image} alt={product.title} className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="p-6 md:p-10">
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-mono font-medium uppercase tracking-[0.25em] text-[#F5F2EB]/50">
                    {product.category} — Size {product.size} — {product.condition}
                  </p>
                  <h3 data-testid="inquiry-product-title" className="mt-2 font-serif text-2xl font-black uppercase tracking-tight">
                    {product.title}
                  </h3>
                  <p data-testid="inquiry-product-price" className="mt-1 font-mono text-lg font-semibold text-[#FF3B1F]">
                    ${product.price}
                  </p>
                </div>
                <button
                  data-testid="inquiry-modal-close"
                  onClick={onClose}
                  className="rounded-full border-2 border-[#F5F2EB] p-2 text-[#F5F2EB] transition-colors hover:bg-[#F5F2EB] hover:text-black"
                >
                  <X size={16} />
                </button>
              </div>

              {product.description && (
                <p data-testid="inquiry-product-description" className="mb-8 text-sm leading-relaxed text-[#F5F2EB]/60">
                  {product.description}
                </p>
              )}

              {sold ? (
                <>
                <div data-testid="inquiry-sold-note" className="-rotate-1 rounded-xl border-2 border-[#F5F2EB] bg-[#FF3B1F] p-6 text-center shadow-[4px_4px_0_#F5F2EB]">
                  <p className="font-serif text-xl font-black uppercase tracking-wide text-white">Gone — re-homed already</p>
                  <p className="mt-2 text-xs font-mono uppercase tracking-[0.2em] text-white/80">Check the rack for what is still here</p>
                </div>
                {alertSent ? (
                  <p data-testid="alert-success" className="rounded-lg border-2 border-[#F5F2EB] bg-[#141414] p-4 text-center text-xs font-mono uppercase tracking-[0.2em] text-emerald-400">
                    You're on the list — you'll hear first
                  </p>
                ) : (
                  <form onSubmit={submitAlert} className="space-y-3" data-testid="restock-alert-form">
                    <p className="text-center text-[10px] font-mono uppercase tracking-[0.25em] text-[#F5F2EB]/40">
                      Want first dibs on similar finds?
                    </p>
                    <div className="flex gap-2">
                      <input
                        required
                        type="email"
                        data-testid="restock-email-input"
                        placeholder="Your email"
                        value={alertEmail}
                        onChange={(e) => setAlertEmail(e.target.value)}
                        className={inputCls}
                      />
                      <button
                        data-testid="restock-alert-submit"
                        className="shrink-0 rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-5 text-[10px] font-mono font-semibold uppercase tracking-[0.2em] text-[#F5F2EB] transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#F5F2EB]"
                      >
                        Notify me
                      </button>
                    </div>
                  </form>
                )}
                </>
              ) : (
                <>
                <div data-testid="buy-now-section">
                  {product.status === "available" && (
                    <button
                      type="button"
                      onClick={buyNow}
                      disabled={buying}
                      data-testid="buy-now-button"
                      className="mb-3 w-full rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] px-6 py-3.5 text-xs font-mono font-semibold uppercase tracking-[0.2em] text-white shadow-[4px_4px_0_#F5F2EB] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      {buying ? "Redirecting to checkout…" : `Buy now — $${product.price}`}
                    </button>
                  )}
                  <p className="mb-4 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-[#F5F2EB]/40">
                    — or make an offer —
                  </p>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input
                      required
                      data-testid="inquiry-name-input"
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                    />
                    <input
                      required
                      type="email"
                      data-testid="inquiry-email-input"
                      placeholder="Email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <input
                    data-testid="inquiry-address-input"
                    placeholder="Shipping address (optional)"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className={inputCls}
                  />
                  <input
                    type="number"
                    min="0"
                    step="1"
                    data-testid="inquiry-offer-input"
                    placeholder={`Your offer (asking $${product.price})`}
                    value={form.offer}
                    onChange={(e) => setForm({ ...form, offer: e.target.value })}
                    className={inputCls}
                  />
                  <textarea
                    rows={3}
                    data-testid="inquiry-message-input"
                    placeholder="Message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={inputCls}
                  />
                  <button
                    disabled={sending}
                    data-testid="inquiry-submit-button"
                    className="w-full bg-[#FF3B1F] px-6 py-3 text-xs font-mono font-medium uppercase tracking-[0.2em] text-white shadow-[4px_4px_0_#F5F2EB] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {sending ? "Sending…" : "Send Inquiry"}
                  </button>
                </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
